/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import axios from 'axios';
import * as Yup from 'yup';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Link from '@/components/Link';
import Button from '@/components/Button';
import FormGroup from '@/components/FormGroup';
import { useApiErrors } from '@/utils/forms';
import ErrorMessage from '@/components/ErrorMessage';
import { APIValidationError } from '@/types/Api';

import AuthLayout from '../../containers/AuthLayout';
import { Form, Footer } from './styles';
import { useAuth } from '../../providers/AuthProvider/hooks';

interface FormData {
  name: string;
  email: string;
  password1: string;
  password2: string;
}

const schema = Yup.object().shape({
  name: Yup.string(),
  email: Yup.string().email('Informe um e-mail válido.'),
  password1: Yup.string()
    .min(6, 'A senha precisa ter pelo menos 6 caracteres.')
    .test(
      'is-numeric',
      'A senha precisa conter pelo menos uma letra.',
      value => !/^[0-9]*$/.test(value || ''),
    ),
  password2: Yup.string().oneOf(
    [Yup.ref('password1')],
    'As senhas não correspondem. Por favor, verifique e tente novamente.',
  ),
});

const SignupScreen: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const { register: doSignUp } = useAuth();

  const {
    formState: { errors },
    register,
    handleSubmit,
    setError,
  } = useForm<FormData>({
    resolver: yupResolver(schema) as Resolver<FormData>,
    criteriaMode: 'all',
  });

  const {
    apiErrors: { non_field_errors },
    setApiErrors,
    clearApiErrors,
  } = useApiErrors<FormData>({}, { setError });

  const onSubmit = async (data: FormData) => {
    try {
      setLoading(true);

      clearApiErrors();
      await doSignUp(data);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err.response && err.response.status === 400) {
          setApiErrors(err.response.data as APIValidationError<FormData>);
        } else {
          setApiErrors({
            non_field_errors: ['Não foi possível realizar o cadastro.'],
          });
        }
        setLoading(false);
      }
    }
  };

  return (
    <AuthLayout title="Crie sua conta">
      <Form onSubmit={handleSubmit(onSubmit)}>
        {non_field_errors && non_field_errors.length > 0 && (
          <ErrorMessage>{non_field_errors[0]}</ErrorMessage>
        )}

        <FormGroup
          label="Nome"
          id="name"
          {...register('name', { required: true })}
        />

        <FormGroup
          label="E-mail"
          errors={
            errors.email && errors.email.message ? [errors.email.message] : []
          }
          {...register('email', { required: true })}
        />

        <FormGroup
          label="Senha"
          type="password"
          id="password1"
          errors={
            errors.password1 && errors.password1.message
              ? [errors.password1.message]
              : []
          }
          {...register('password1', { required: true })}
        />

        <FormGroup
          label="Confirme sua senha"
          type="password"
          id="password2"
          errors={
            errors.password2 && errors.password2.message
              ? [errors.password2.message]
              : []
          }
          {...register('password2', { required: true })}
        />

        <Button type="submit" disabled={loading} loading={loading}>
          Criar minha conta e entrar
        </Button>
      </Form>

      <Footer>
        Já possui uma conta?
        <Link to="/"> Entre no Medflow</Link>
      </Footer>
    </AuthLayout>
  );
};

export default SignupScreen;
